import { useState, useEffect } from 'react';
import { ArrowLeftIcon, CheckIcon, XIcon, InfoIcon } from '@phosphor-icons/react';
import { QuestionItem } from '../../shared/question-item';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from '../../../components/ui/pagination';
import { PieChart, Pie, Cell } from 'recharts';
import { useSelector, useDispatch } from 'react-redux';
import { toggleSimplifyMode } from '../../../../../../apps/frontend/src/redux/slices/globalSlice';
import { getCustomNotebookDetails, updateNotebook, archiveNotebook } from '../../../services/notebooks';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../components/ui/select';
import NotebookDetailLoader from '../../../components/loaders/NotebookLoader';
import { SimplifyModeToggle } from '../../../components/simplifymode/SimplifyMode';
import { ContextMenu } from '../../../components/contextmenu/ContextMenu';
import { EditModal } from '../../../components/modals/EditModal/EditModal';
import { DeleteModal } from '../../../components/modals/DeleteModal/DeleteModal';
import { PencilSimpleIcon, TrashIcon } from '@phosphor-icons/react';
import NotebookQuestionDrawer from '../../../components/drawers/NotebookQuestionDrawer';
import { useRef } from 'react';
import { Tooltip } from 'recharts';

export const NotebookDetail = ({ notebook, onBack, onNotebookUpdate, onNotebookDelete }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState('All Qs');
  const [selectedTest, setSelectedTest] = useState('all');
  const [isMobile, setIsMobile] = useState(false);
  const [sortBy, setSortBy] = useState('Default');
  const [questions, setQuestions] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const user = JSON.parse(localStorage.getItem('userProfile'));
  const [autoStartTimer, setAutoStartTimer] = useState(user?.settings?.toggleQuestionTimer);
  const [stats, setStats] = useState({
    correct: 0,
    wrong: 0,
    unattempted: 0,
  });
  const [totalPages, setTotalPages] = useState(1);
  const [testFilters, setTestFilters] = useState([]);
  const dispatch = useDispatch();
  const simplifyMode = useSelector((state) => state.global.simplifyMode);
  const [isEditing, setIsEditing] = useState(false);
  const [notebookName, setNotebookName] = useState(notebook?.name || '');
  const [currentNotebook, setCurrentNotebook] = useState(notebook);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(null);
  const [isQuestionDrawerOpen, setIsQuestionDrawerOpen] = useState(false);
  const ignoreNextClickRef = useRef(false);
  const [lastBookmarkClickTime, setLastBookmarkClickTime] = useState(0);

  const contextMenuItems = [
    {
      id: 'rename',
      label: 'Rename',
      icon: PencilSimpleIcon,
      action: 'rename',
      hoverBg: 'q3-surface-dimmer',
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: TrashIcon,
      action: 'delete',
      hoverBg: 'q3-danger-light',
    },
  ];

  const handleContextMenuAction = (action) => {
    switch (action) {
      case 'rename':
        setIsEditing(true);
        break;
      case 'delete':
        setShowDeleteModal(true);
        break;
      default:
        break;
    }
  };

  const filters = [
    { id: 'all', label: 'All Qs' },
    { id: 'attempted-correct', label: 'Attempted correct' },
    { id: 'attempted-wrong', label: 'Attempted wrong' },
    { id: 'not-attempted', label: 'Not attempted' },
  ];

  const sortOptions = ['Default', 'Newest First', 'Oldest First', 'A-Z', 'Z-A'];

  const COLORS = ['var(--q3-danger-normal)', 'var(--q3-success-normal)', 'var(--q3-neutral-light)'];

  const formatAndUppercase = (text) => {
    if (!text || typeof text !== 'string') return text;
    return text.replace(/([a-z])([A-Z])/g, '$1 $2').toUpperCase();
  };

  const mapQuestionData = (apiQuestion, index, testsArray) => {
    const question = apiQuestion.question || {};
    const subject = apiQuestion.subject || {};
    const chapter = apiQuestion.chapter || {};
    const testTitle = apiQuestion?.testTitle
      ? apiQuestion?.testTitle
      : testsArray && testsArray.length > 0
        ? testsArray[0].title
        : 'Unknown Test';
    const testId = apiQuestion.test || 'Unknown Test ID';

    return {
      id: `Q${index + 1}`,
      testId,
      question: question.question?.text || 'Question text not available',
      questionid: apiQuestion.question?._id || 'Unknown ID',
      status:
        formatAndUppercase(apiQuestion.status === 'notVisited' ? 'notAttempted' : apiQuestion.status) || 'UNKNOWN',
      type: apiQuestion.type || 'UNKNOWN',
      testInfo: testTitle,
      note: apiQuestion.notes || null,
      isBookmarked: true,
      timeTaken: apiQuestion.timeTaken || 0,
      level: question.level || 1,
      subject: subject.title || 'Unknown Subject',
      chapter: chapter.title || 'Unknown Chapter',
      correctAnswer: question.options?.find((opt) => opt.isCorrect)?.text || 'No correct answer',
      userAnswer: apiQuestion.optionOrSoln?.value || null,
      isCorrect: apiQuestion.isCorrect || false,
      createdAt: apiQuestion.createdAt || new Date().toISOString(),
      rawData: apiQuestion,
      correctValue: question.correctValue || null,
      numericalLowerLimit: question.numericalLowerLimit || null,
      numericalUpperLimit: question.numericalUpperLimit || null,
    };
  };

  // Calculate filter counts from API response totals
  const computeAllFilterCounts = (responseData) => {
    const total =
      (responseData.totalCorrect || 0) + (responseData.totalIncorrect || 0) + (responseData.totalUnattempted || 0);
    return {
      'All Qs': total,
      'Attempted correct': responseData.totalCorrect || 0,
      'Attempted wrong': responseData.totalIncorrect || 0,
      'Not attempted': responseData.totalUnattempted || 0,
    };
  };

  // Calculate stats for pie chart from API response totals
  const computeStats = (responseData) => {
    return {
      correct: responseData.totalCorrect || 0,
      wrong: responseData.totalIncorrect || 0,
      unattempted: responseData.totalUnattempted || 0,
    };
  };

  const [filterCounts, setFilterCounts] = useState({
    'All Qs': 0,
    'Attempted correct': 0,
    'Attempted wrong': 0,
    'Not attempted': 0,
  });

  // Fetch all questions first for counting, then fetch filtered questions for display
  const fetchAllQuestions = async () => {
    try {
      const params = {
        offset: 0,
        limit: 1000, // Get all questions for counting
      };

      if (selectedTest !== 'all') {
        params.test = selectedTest;
      }

      const response = await getCustomNotebookDetails(currentNotebook.id, params);
      const apiQuestions = response.data?.answers || [];
      const testsArray = response.data?.tests || [];

      // Map all questions
      const mappedQuestions = apiQuestions.map((question, index) => mapQuestionData(question, index, testsArray));

      // Set all questions
      setAllQuestions(mappedQuestions);

      // Calculate filter counts and stats from API response totals
      const counts = computeAllFilterCounts(response.data);
      const statsData = computeStats(response.data);

      setFilterCounts(counts);
      setStats(statsData);
      setTestFilters(testsArray);

      return mappedQuestions;
    } catch (error) {
      return [];
    }
  };

  const fetchNotebookDetails = async () => {
    try {
      if (initialLoad) {
        setLoading(true);
        // First fetch all questions for counting
        await fetchAllQuestions();
      }

      const params = {
        offset: (currentPage - 1) * 10,
        limit: 10,
      };

      // Add filter params based on activeFilter
      if (activeFilter === 'Attempted correct') {
        params.correct = true;
      } else if (activeFilter === 'Attempted wrong') {
        params.incorrect = true;
      } else if (activeFilter === 'Not attempted') {
        params.unattempted = true;
      }

      if (selectedTest !== 'all') {
        params.test = selectedTest;
      }

      const response = await getCustomNotebookDetails(currentNotebook.id, params);
      const apiQuestions = response.data?.answers || [];
      const testsArray = response.data?.tests || [];

      // Map questions for current page
      const mappedQuestions = apiQuestions.map((question, index) => mapQuestionData(question, index, testsArray));
      setQuestions(mappedQuestions);
      if (isMobile || activeFilter !== 'All Qs') {
        setFilteredQuestions(mappedQuestions);
      } else {
        // Apply client-side filtering only for desktop "All Qs" case
        const filtered = filterQuestions(mappedQuestions);
        const sorted = applySorting(filtered);
        setFilteredQuestions(sorted);
      }

      setTotalPages(response.totalPages || 1);
    } catch (error) {
      setQuestions([]);
      setFilteredQuestions([]);
    } finally {
      if (initialLoad) {
        setInitialLoad(false);
        setLoading(false);
      }
    }
  };

  const handleFilterChange = (filterLabel) => {
    const label = filterLabel.split(' (')[0];

    setActiveFilter(label);
    setCurrentPage(1);
  };

  const filterQuestions = (questionsToFilter) => {
    if (activeFilter === 'All Qs') {
      return questionsToFilter;
    } else if (activeFilter === 'Attempted correct') {
      return questionsToFilter.filter((q) => q.isCorrect);
    } else if (activeFilter === 'Attempted wrong') {
      return questionsToFilter.filter((q) => q.userAnswer !== null && !q.isCorrect);
    } else if (activeFilter === 'Not attempted') {
      return questionsToFilter.filter((q) => q.userAnswer === null);
    }
    return questionsToFilter;
  };

  const applySorting = (questionsToSort) => {
    switch (sortBy) {
      case 'Newest First':
        return [...questionsToSort].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      case 'Oldest First':
        return [...questionsToSort].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      case 'A-Z':
        return [...questionsToSort].sort((a, b) => a.question.localeCompare(b.question));
      case 'Z-A':
        return [...questionsToSort].sort((a, b) => b.question.localeCompare(a.question));
      default:
        return questionsToSort;
    }
  };

  useEffect(() => {
    if (!isMobile && activeFilter === 'All Qs') {
      const filtered = filterQuestions(questions);
      const sorted = applySorting(filtered);
      setFilteredQuestions(sorted);
    } else if (!isMobile) {
      // For desktop with filters, still apply sorting
      const sorted = applySorting(questions);
      setFilteredQuestions(sorted);
    }
    // For mobile, filteredQuestions is already set in fetchNotebookDetails
  }, [activeFilter, sortBy, questions, isMobile]);

  // Calculate percentage data from stats
  const calculatePercentageData = () => {
    const total = stats.correct + stats.wrong + stats.unattempted;
    if (total === 0) return [];

    return [
      { name: 'Attempted wrong', value: Math.round((stats.wrong / total) * 100) },
      { name: 'Attempted correct', value: Math.round((stats.correct / total) * 100) },
      { name: 'Not Attempted', value: Math.round((stats.unattempted / total) * 100) },
    ];
  };

  const percentageData = calculatePercentageData();

  const handleUpdateNotebook = async () => {
    try {
      const response = await updateNotebook(currentNotebook.id, notebookName);
      if (response.success) {
        const updatedNotebook = {
          ...currentNotebook,
          name: notebookName,
          title: notebookName,
        };
        setCurrentNotebook(updatedNotebook);
        if (onNotebookUpdate) {
          onNotebookUpdate(updatedNotebook);
        }
      }
    } catch (error) {
      setNotebookName(currentNotebook.name);
    } finally {
      setIsEditing(false);
    }
  };

  const handleQuestionClick = (index) => {
    if (ignoreNextClickRef.current) {
      ignoreNextClickRef.current = false;
      return;
    }
    setSelectedQuestionIndex(index);
    setIsQuestionDrawerOpen(true);
  };

  const handleQuestionChange = (newIndex) => {
    setSelectedQuestionIndex(newIndex);
  };

  const handleBookmarkClick = () => {
    setLastBookmarkClickTime(Date.now());
  };

  const handleArchiveNotebook = async () => {
    try {
      const response = await archiveNotebook(currentNotebook.id);
      if (response.success) {
        if (onNotebookDelete) {
          onNotebookDelete(currentNotebook.id);
        }

        onBack();
      }
    } catch (error) {
    } finally {
      setShowDeleteModal(false);
    }
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch all questions when test filter changes
  useEffect(() => {
    if (currentNotebook?.id) {
      fetchAllQuestions();
    }
  }, [currentNotebook, selectedTest]);

  // Fetch paginated questions when page or filter changes
  useEffect(() => {
    if (currentNotebook?.id) {
      fetchNotebookDetails();
    }
  }, [currentNotebook, currentPage, activeFilter, selectedTest]);

  useEffect(() => {
    if (notebook) {
      setCurrentNotebook(notebook);
      setNotebookName(notebook.name || '');
    }
  }, [notebook]);

  if (loading && initialLoad) {
    return <NotebookDetailLoader />;
  }

  const renderCustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 rounded px-2 py-1 bg-[var(--q3-surface-default)] shadow text-xs font-medium border border-[var(--q3-stroke-normal)] whitespace-nowrap z-10">
          {`${payload[0].name}: ${payload[0].value.toFixed(2)}%`}
        </div>
      );
    }
    return null;
  };

  // Edit Name Modal
  const EditNameModal = () => (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{
        background: 'rgba(16, 24, 40, 0.70)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
    >
      <div
        className="p-6 shadow-2xl w-[95%] sm:w-[440px] md:w-[460px] max-w-[95vw]"
        style={{
          background: 'var(--q3-surface-default)',
          borderRadius: '16px',
        }}
      >
        <h3 className="text-base font-semibold mb-3" style={{ color: 'var(--q3-neutral-default)' }}>
          Notebook Name
        </h3>

        <input
          type="text"
          value={notebookName}
          onChange={(e) => setNotebookName(e.target.value)}
          className="w-full p-3 mb-5 text-sm rounded outline-none focus:border-[1px]"
          style={{
            border: '1px solid #D0D5DD', // fallback/default border
            color: 'var(--q3-neutral-default)',
            borderRadius: '8px',
          }}
          onFocus={(e) => (e.target.style.border = '2px solid var(--q3-accent-normal)')}
          onBlur={(e) => (e.target.style.border = '1px solid #D0D5DD')}
          placeholder="Enter notebook name"
        />

        <div className="flex justify-end gap-3">
          <button
            onClick={() => {
              setIsEditing(false);
              setNotebookName(currentNotebook.name);
            }}
            style={{
              borderRadius: '360px',
              padding: '8px 20px',
              background: 'transparent',
              color: 'var(--q3-neutral-default)',
              border: '1px solid #D0D5DD',
            }}
          >
            Cancel
          </button>

          <button
            onClick={() => {
              handleUpdateNotebook();
              setIsEditing(false);
            }}
            style={{
              borderRadius: '360px',
              padding: '8px 20px',
              background: 'var(--q3-accent-normal)',
              color: '#fff',
            }}
          >
            Update Name
          </button>
        </div>
      </div>
    </div>
  );

  // Delete Confirmation Modal
  const DeleteConfirmationModal = () => (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{
        background: 'rgba(16, 24, 40, 0.70)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
    >
      <div
        className="p-6 shadow-2xl w-[95%] sm:w-[440px] md:w-[460px] max-w-[95vw]"
        style={{
          background: 'var(--q3-surface-default)',
          borderRadius: '16px',
        }}
      >
        <h3 className="text-base font-semibold mb-4" style={{ color: 'var(--q3-neutral-default)' }}>
          Delete Notebook
        </h3>

        <p className="mb-6 text-sm" style={{ color: 'var(--q3-neutral-default)' }}>
          This will delete all the questions present in the notebook, Are you sure you want to delete it?
        </p>

        <div className="flex justify-end gap-3">
          <button
            onClick={() => setShowDeleteModal(false)}
            style={{
              borderRadius: '360px',
              padding: '8px 20px',
              background: 'transparent',
              color: 'var(--q3-neutral-default)',
              border: '1px solid #D0D5DD',
            }}
          >
            Cancel
          </button>

          <button
            onClick={() => {
              handleArchiveNotebook();
              setShowDeleteModal(false);
            }}
            style={{
              borderRadius: '360px',
              padding: '8px 20px',
              background: 'var(--destructive)',
              color: '#fff',
            }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div className="min-h-screen bg-[var(--q3-surface-default)] pb-6">
        {/* Modals - Using exact same components as laptop view */}
        {isEditing && (
          <EditModal
            isOpen={isEditing}
            onClose={() => {
              setIsEditing(false);
              setNotebookName(currentNotebook.name);
            }}
            title="Notebook Name"
            value={notebookName}
            onChange={setNotebookName}
            onSave={handleUpdateNotebook}
          />
        )}
        {showDeleteModal && (
          <DeleteModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={handleArchiveNotebook}
            title="Delete Notebook"
            description="This will delete all the questions present in the notebook, Are you sure you want to delete it?"
          />
        )}

        {/* Header - Matching laptop view structure */}
        <div className="sticky top-0 bg-[var(--q3-surface-default)] border-b border-[var(--q3-stroke-normal)] px-4 py-3 z-10">
          <div className="flex items-center gap-3 mb-2">
            <button onClick={onBack} className="p-2 -ml-2">
              <ArrowLeftIcon size={20} className="text-[var(--q3-neutral-default)]" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-[var(--q3-neutral-default)] truncate">{currentNotebook?.name}</h1>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setIsEditing(true)}
                className="p-2 rounded-full bg-[var(--q3-surface-dimmer)] hover:bg-[var(--q3-surface-dimmer)] cursor-pointer"
              >
                <PencilSimpleIcon size={20} />
              </button>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="p-2 rounded-full bg-[var(--q3-danger-light)] hover:bg-[var(--q3-danger-light)] cursor-pointer"
              >
                <TrashIcon size={20} />
              </button>
            </div>
          </div>

          {/* Filters - Matching laptop view dropdown and styling */}
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
            <Select value={selectedTest} onValueChange={setSelectedTest}>
              <SelectTrigger className="rounded-full text-xs border text-[var(--q3-neutral-default)] hover:bg-[var(--q3-stroke-normal)] px-3 py-1.5 h-auto">
                <SelectValue placeholder="Test" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Test</SelectItem>
                {testFilters.map((test) => (
                  <SelectItem key={test._id} value={test._id}>
                    {test.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {filters.map((filter) => {
              const baseLabel = filter.label.split(' (')[0];
              const count = filterCounts[baseLabel] || 0;
              return (
                <button
                  key={filter.id}
                  onClick={() => handleFilterChange(baseLabel)}
                  className={`relative flex items-center gap-1 px-3 py-1.5 rounded-full text-xs transition-colors whitespace-nowrap
                  ${
                    activeFilter === baseLabel
                      ? 'bg-[var(--q3-accent-normal)] text-white '
                      : 'text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] hover:bg-[var(--q3-surface-dimmest)] border border-[var(--q3-stroke-normal)]'
                  }`}
                >
                  {`${baseLabel} (${count})`}
                  {activeFilter === baseLabel && <CheckIcon size={14} weight="bold" className="ml-1" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Stats Card - Exact same layout as laptop but stacked */}
        {percentageData.length > 0 && (
          <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-lg px-4 py-3 mb-4 mx-4 mt-4">
            <div className="flex flex-col gap-4">
              <div>
                <h3 className="text-base font-semibold text-[var(--q3-neutral-default)] mb-1">Qs in this notebook</h3>
                <p className="text-xs text-[var(--q3-neutral-light)]">
                  This pie chart shows your attempt status of questions added in this notebook
                </p>
              </div>

              <div className="flex items-center gap-4 -mt-4">
                <div className="w-20 h-20 flex-shrink-0">
                  <PieChart width={80} height={80} className="mt-10">
                    <Pie data={percentageData} innerRadius={24} outerRadius={40} dataKey="value" stroke="none">
                      {percentageData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index]} />
                      ))}
                    </Pie>
                    <Tooltip content={renderCustomTooltip} />
                  </PieChart>
                </div>

                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--q3-stroke-normal)] rounded bg-[var(--q3-surface-default)]">
                    <XIcon size={16} weight="bold" className="text-red-400" />
                    <span className="text-xs text-[var(--q3-neutral-light)] whitespace-nowrap">
                      Attempted wrong{' '}
                      <span className="font-semibold text-[var(--q3-neutral-default)]">
                        ({percentageData[0]?.value || 0}%)
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--q3-stroke-normal)] rounded bg-[var(--q3-surface-default)]">
                    <CheckIcon size={16} weight="bold" className="text-green-400" />
                    <span className="text-xs text-[var(--q3-neutral-light)] whitespace-nowrap">
                      Attempted correct{' '}
                      <span className="font-semibold text-[var(--q3-neutral-default)]">
                        ({percentageData[1]?.value || 0}%)
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--q3-stroke-normal)] rounded bg-[var(--q3-surface-default)]">
                    <InfoIcon size={16} weight="bold" className="text-gray-400" />
                    <span className="text-xs text-[var(--q3-neutral-light)] whitespace-nowrap">
                      Not Attempted{' '}
                      <span className="font-semibold text-[var(--q3-neutral-default)]">
                        ({percentageData[2]?.value || 0}%)
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="px-4">
          <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-lg">
            <h3 className="text-base font-semibold text-[var(--q3-neutral-default)] p-3 border-b border-[var(--q3-stroke-normal)]">
              All Bookmarked Qs
            </h3>
            <div className="divide-y divide-[var(--q3-stroke-normal)]">
              {filteredQuestions.length > 0 ? (
                filteredQuestions.map((question, index) => (
                  <div
                    key={question.id}
                    className="p-3 hover:bg-[var(--q3-surface-dim)] cursor-pointer"
                    onClick={(e) => {
                      if (!e.target.closest('.bookmark-button')) {
                        handleQuestionClick(index);
                      }
                    }}
                  >
                    <QuestionItem
                      {...question} // this now includes testId from apiQuestion.test
                      packId={notebook.packId}
                      notebookData={notebook}
                      className="bookmark-button"
                      onBookmarkOpen={() => {
                        ignoreNextClickRef.current = true;
                      }}
                    />
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-[var(--q3-neutral-light)]">
                    {activeFilter === 'All Qs' && questions.length === 0
                      ? 'No questions found'
                      : `No ${activeFilter.toLowerCase()} questions found`}
                  </p>
                  {activeFilter !== 'All Qs' && (
                    <button
                      onClick={() => handleFilterChange('All Qs')}
                      className="mt-2 px-4 py-2 text-sm bg-[var(--q3-accent-normal)] text-white rounded-lg"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {totalPages > 1 && (
          <div className="mt-6 px-4">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="h-9 w-9 p-0"
                  />
                </PaginationItem>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <PaginationItem key={page}>
                    <PaginationLink
                      isActive={page === currentPage}
                      onClick={() => setCurrentPage(page)}
                      className="h-9 w-9 p-0"
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="h-9 w-9 p-0"
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}

        {/*        
     <div className="fixed bottom-20 md:bottom-6 left-0 right-0 flex justify-center px-4">
  <div
    className="inline-flex min-h-[48px] px-4 py-2 items-center gap-3 rounded-[12px] cursor-pointer transition-all duration-300 border bg-[var(--q3-surface-glass)] backdrop-blur-sm"
    style={{
      borderImage: 'linear-gradient(to right, #FF6B00, var(--q3-stroke-light)) 1',
      borderImageSlice: 1,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderRadius: '12px',
      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
    }}
    onClick={() => dispatch(toggleSimplifyMode())}
  >
    <span className="text-sm font-medium text-[var(--q3-neutral-default)]">
      Simplify Mode is {simplifyMode ? 'On' : 'Off'}
    </span>
    <div
      className={`w-12 h-6 rounded-full relative transition-colors duration-300 ${simplifyMode ? 'bg-[var(--q3-accent-normal)]' : 'bg-[var(--q3-stroke-normal)]'}`}
    >
      <div
        className={`w-5 h-5 bg-white rounded-full absolute top-0.5 shadow-md transition-transform duration-300 ${simplifyMode ? 'translate-x-[26px]' : 'translate-x-0.5'}`}
      />
    </div>
  </div>
</div> */}

        {questions.length > 0 && (
          <div className="flex justify-center mt-8">
            <SimplifyModeToggle />
          </div>
        )}

        {isQuestionDrawerOpen && (
          <NotebookQuestionDrawer
            isOpen={isQuestionDrawerOpen}
            onClose={() => setIsQuestionDrawerOpen(false)}
            questions={filteredQuestions}
            currentQuestionIndex={selectedQuestionIndex}
            onQuestionChange={handleQuestionChange}
            testId={notebook.testId}
            packId={notebook.packId}
            autoStartTimer={autoStartTimer}
            onAutoStartTimerChange={setAutoStartTimer}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex-1 p-6">
      {/* Edit Modal */}
      <EditModal
        isOpen={isEditing}
        onClose={() => {
          setIsEditing(false);
          setNotebookName(currentNotebook.name);
        }}
        title="Notebook Name"
        value={notebookName}
        onChange={setNotebookName}
        onSave={handleUpdateNotebook}
      />

      {/* Delete Modal */}
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleArchiveNotebook}
        title="Delete Notebook"
        description="This will delete all the questions present in the notebook, Are you sure you want to delete it?"
      />

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex flex-col gap-3">
          <button onClick={onBack} className="w-fit p-2 rounded-full bg-[var(--q3-surface-dimmer)]">
            <ArrowLeftIcon size={20} />
          </button>
          <h1
            className="text-[var(--q3-neutral-default)] font-bold"
            style={{
              fontFamily: 'Inter',
              fontSize: 'var(--font-display-2xl-font_size, 24px)',
              fontWeight: 'var(--font-display-2xl-font_weight, 700)',
              lineHeight: 'var(--font-display-2xl-line_height, 32px)',
            }}
          >
            {currentNotebook?.name}
          </h1>
        </div>

        {/* Right: Edit/Delete buttons */}
        <div className="flex items-start gap-2 pt-2 c">
          <button
            onClick={() => setIsEditing(true)}
            className="p-2 rounded-full bg-[var(--q3-surface-dimmer)] hover:bg-[var(--q3-surface-dimmer)] cursor-pointer"
          >
            <PencilSimpleIcon size={20} />
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="p-2 rounded-full bg-[var(--q3-danger-light)] hover:bg-[var(--q3-danger-light)] cursor-pointer"
          >
            <TrashIcon size={20} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto whitespace-nowrap">
        <Select value={selectedTest} onValueChange={setSelectedTest}>
          <SelectTrigger className="rounded-full text-xs border text-[var(--q3-neutral-default)] hover:bg-[var(--q3-stroke-normal)] px-3 py-1.5">
            <SelectValue placeholder="Test" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Test</SelectItem>
            {testFilters.map((test) => (
              <SelectItem key={test._id} value={test._id}>
                {test.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="h-5 w-px bg-[var(--q3-stroke-normal)] mx-2" />

        {filters.map((filter) => {
          const baseLabel = filter.label.split(' (')[0];
          const count = filterCounts[baseLabel] || 0;
          return (
            <button
              key={filter.id}
              onClick={() => handleFilterChange(baseLabel)}
              className={`relative flex items-center gap-1 px-4 py-2 pr-4 rounded-full text-sm transition-colors whitespace-nowrap
      ${
        activeFilter === baseLabel
          ? 'bg-[var(--q3-accent-normal)] text-white '
          : 'text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] hover:bg-[var(--q3-surface-dimmest)] border border-[var(--q3-stroke-normal)]'
      }`}
            >
              {`${baseLabel} (${count})`}
              {activeFilter === baseLabel && <CheckIcon size={14} weight="bold" className="ml-1" />}
            </button>
          );
        })}
      </div>

      {/* Stats Card */}

      {percentageData.length > 0 && (
        <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-lg px-6 py-0 mb-4 ">
          <div className="flex items-center justify-between gap-6">
            {/* Left Text Section */}
            <div className="min-w-[200px]">
              <h3 className="text-lg font-semibold text-[var(--q3-neutral-default)] mb-1">Qs in this notebook</h3>
              <p className="text-xs text-[var(--q3-neutral-light)] m-0 whitespace-normal">
                This pie chart shows your attempt status of questions
                <br />
                added in this notebook
              </p>
            </div>

            {/* Pie Chart */}
            <div className="relative w-20 h-20 flex items-center justify-center ml-auto select-none touch-none">
              {percentageData.length > 0 && (
                <>
                  <PieChart
                    width={80}
                    height={80}
                    className="select-none touch-none"
                    style={{ userSelect: 'none', WebkitUserSelect: 'none', outline: 'none' }}
                  >
                    <Pie
                      data={percentageData}
                      innerRadius={24}
                      outerRadius={40}
                      dataKey="value"
                      stroke="none"
                      className="select-none touch-none"
                      style={{ userSelect: 'none', WebkitUserSelect: 'none', outline: 'none' }}
                    >
                      {percentageData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                          className="select-none touch-none"
                          style={{ userSelect: 'none', WebkitUserSelect: 'none', outline: 'none' }}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={renderCustomTooltip} />
                  </PieChart>
                </>
              )}
            </div>

            {/* Status Boxes */}
            <div className="flex flex-row gap-3 text-sm">
              <div className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--q3-stroke-normal)] rounded bg-[var(--q3-surface-default)]">
                <XIcon size={16} weight="bold" className="text-red-400" />
                <span className="text-xs text-[var(--q3-neutral-light)] whitespace-nowrap">
                  Attempted wrong{' '}
                  <span className="font-semibold text-[var(--q3-neutral-default)]">
                    ({percentageData[0]?.value || 0}%)
                  </span>
                </span>
              </div>

              <div className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--q3-stroke-normal)] rounded bg-[var(--q3-surface-default)]">
                <CheckIcon size={16} weight="bold" className="text-green-400" />
                <span className="text-xs text-[var(--q3-neutral-light)] whitespace-nowrap">
                  Attempted correct{' '}
                  <span className="font-semibold text-[var(--q3-neutral-default)]">
                    ({percentageData[1]?.value || 0}%)
                  </span>
                </span>
              </div>

              <div className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--q3-stroke-normal)] rounded bg-[var(--q3-surface-default)]">
                <InfoIcon size={16} weight="bold" className="text-gray-400" />
                <span className="text-xs text-[var(--q3-neutral-light)] whitespace-nowrap">
                  Not Attempted{' '}
                  <span className="font-semibold text-[var(--q3-neutral-default)]">
                    ({percentageData[2]?.value || 0}%)
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Questions List */}
      <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-lg mb-6 ">
        <h3 className="text-lg font-semibold text-[var(--q3-neutral-default)]  p-4 border-b border-[var(--q3-stroke-normal)]">
          All Bookmarked Qs
        </h3>
        <div className="space-y-0 ">
          {questions.length > 0 ? (
            questions.map((question, index) => {
              return (
                <div
                  key={question.id}
                  className="pb-4 last:border-b-0 last:pb-0 p-2 border-b border-[var(--q3-stroke-normal)] hover:bg-[var(--q3-surface-dim)] cursor-pointer"
                  onClick={(e) => {
                    // Only trigger if the click wasn't on the bookmark button
                    if (!e.target.closest('.bookmark-button')) {
                      handleQuestionClick(index);
                    }
                  }}
                >
                  <QuestionItem
                    {...question} // this now includes testId from apiQuestion.test
                    packId={notebook.packId}
                    notebookData={notebook}
                    className="bookmark-button"
                    onBookmarkOpen={() => {
                      ignoreNextClickRef.current = true;
                    }}
                  />
                </div>
              );
            })
          ) : (
            <div className="text-center py-8 ">
              <p className="text-[var(--q3-neutral-light)] ">
                {activeFilter === 'All Qs' && questions.length === 0
                  ? 'No questions found'
                  : `No ${activeFilter.toLowerCase()} questions found`}
              </p>
              {activeFilter !== 'All Qs' && (
                <button
                  onClick={() => handleFilterChange('All Qs')}
                  className="mt-2 px-4 py-2 text-sm bg-[var(--q3-accent-normal)] text-white rounded-lg"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {filteredQuestions.length > 0 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              />
            </PaginationItem>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <PaginationItem key={page}>
                <PaginationLink isActive={page === currentPage} onClick={() => setCurrentPage(page)}>
                  {page}
                </PaginationLink>
              </PaginationItem>
            ))}
            <PaginationItem>
              <PaginationNext
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      {/* Simplify Mode Toggle */}
      {questions.length > 0 && (
        <div className="flex justify-center mt-8">
          <SimplifyModeToggle />
        </div>
      )}

      {/* Question Drawer */}
      {isQuestionDrawerOpen && selectedQuestionIndex !== null && (
        <NotebookQuestionDrawer
          isOpen={isQuestionDrawerOpen}
          onClose={() => setIsQuestionDrawerOpen(false)}
          questions={questions}
          currentQuestionIndex={selectedQuestionIndex}
          onQuestionChange={handleQuestionChange}
          testId={notebook.testId}
          packId={notebook.packId}
          autoStartTimer={autoStartTimer}
          onAutoStartTimerChange={setAutoStartTimer}
        />
      )}
    </div>
  );
};
